<template>
  <section class="registerPage">
    <div class="registrationContainer" v-if="step > 2 && step < 8">
      <div class="registrationContainer__Inner">
        <div class="registrationProgress">
          <div class="registrationProgress__BarBox">
            <div class="registrationProgress__Bar" :style="`width: ${(step / stepCount) * 100}%`" />
          </div>
          <span class="registrationProgress__Nums">{{ step }} / {{ stepCount }}</span>
        </div>
        <p class="registerPage__OrgName" v-if="registerData.org.full_name">
          New organisation:
          <span>{{ registerData.org.full_name }}</span>
        </p>
      </div>
    </div>
    <form v-if="step < 8" class="registerForm" @submit.prevent="register()">
      <div class="registrationContainer">
        <!-- Texts -->
        <div class="registrationColumn">
          <div class="registrationColumn__Inner">
            <template v-if="step === 1">
              <h2 class="registrationPrompt__Title">First, let's check if your organisation is already on POP!</h2>
              <p class="registrationPrompt__Text">
                If you're already inside POP, you can request invite from your administrator, or reset your password. If
                not, we'll lead you through registration process.
              </p>
              <p class="registrationPrompt__Text">
                Registration takes less then 10 minutes. You'll need to provide us with contact information, one
                official document about your ogranisation and social links.
              </p>
            </template>
            <template v-if="step === 2">
              <h2 class="registrationPrompt__Title">We're happy to have you here!</h2>
              <p class="registrationPrompt__Text">
                This platform will help you build stronger campaigns and connect you to other organisations working for
                the same goals: social and ecological justice and equity.
              </p>
              <div class="registrationIntro">
                <h3 class="registrationIntro__Title">WHAT YOU'LL NEED</h3>
                <div class="registrationIntro__List">
                  <div class="registrationIntro__TextBox">
                    <h4>Short paragraph on what you do</h4>
                    <p>To email you and country to contact you and set the interface language</p>
                  </div>
                  <div class="registrationIntro__TextBox">
                    <h4>One official document</h4>
                    <p>Cerificate of registration, permit of operation, statues, constitutions.</p>
                  </div>
                  <div class="registrationIntro__TextBox">
                    <h4>Social media and website links</h4>
                    <p>Cerificate of registration, permit of operation, statues, constitutions.</p>
                  </div>
                </div>
              </div>
            </template>
            <template v-if="step === 3">
              <h2 class="registrationPrompt__Title">Do we share the same values?</h2>
              <p class="registrationPrompt__Text">
                People Over Profit is laid on the foundation of some core values - social and ecological justice,
                equity, democracy, public service, worker rights.
              </p>

              <p class="registrationPrompt__Text">
                Our value system assumes that all people on this planet have the same rights, as enshrined in the UN
                Charter and other instruments. Our governments are tasked with the responsibilities to respect, protect
                and fulfill these rights.
              </p>

              <p class="registrationPrompt__Text">
                Many of these human rights - to health, water, education - are best ensured by public services, designed
                for, and responsive to the needs of people. For-profit corporations focus above all on maximising
                profits, and not on fulfilling human rights or other social or ecological concerns.
              </p>

              <p class="registrationPrompt__Text">
                Our governments must respond to the needs of the people - and stop catering to big corporations and the
                richest. People must participate in strengthening our democratic institutions.
              </p>

              <p class="registrationPrompt__Text">
                Since too many years, and in too many societies, women suffer discrimination and inequality. Social
                justice and equity can't be achieved without gender equity, in the workplace, at home and across our
                many institutions.
              </p>

              <p class="registrationPrompt__Text">
                The climate and Covid crises demonstrate the need to respect the rights of the planet's living organisms
                - animals, plants, waters, etc. if only to protect our own lives.
              </p>
            </template>
            <template v-if="step === 4">
              <h2 class="registrationPrompt__Title">Great, now let's get some basic info about you.</h2>
              <p class="registrationPrompt__Text">
                You will register as administrator for your organisation. You'll be able to manage your team profile and
                settings, to publish content and invite more users from your organisation. You must ensure that all
                postings by your registered participants comply with your standards, as your organisation is
                responsible/liable for everything you post here.
              </p>
              <p class="registrationPrompt__Text registrationPrompt__Text--Small">
                If you need to change administrator, you must ensure that they know these rights and responsibilities.
                You can add to or edit this information after you login for the first time.
              </p>
            </template>
            <template v-if="step === 5">
              <h2 class="registrationPrompt__Title">Now, let's talk about your organisation.</h2>
              <p class="registrationPrompt__Text">
                We need to know what type of organisation you represent. Where are you situated, what languages does
                your team speak, and which issues are you fighting for.
              </p>
              <p class="registrationPrompt__Text registrationPrompt__Text--Small">
                You'll have to share at least one document proving your legal status. This will ensure you can be
                responsible for the content published on the platform.
              </p>
            </template>
            <template v-if="step === 6">
              <h2 class="registrationPrompt__Title">Well done! Let's finish with contact details..</h2>
              <p class="registrationPrompt__Text">
                Thank you! Two more steps and we're done! We'll use your contact information to check your presence on
                the internet.
              </p>
              <p class="registrationPrompt__Text registrationPrompt__Text--Small">
                If your campaign or organisation is just starting and you still don't have any digital presence, we'll
                call you or arrange an online meeting and get to know each other.
              </p>
            </template>
            <template v-if="step === 7">
              <h2 class="registrationPrompt__Title">Before we finish, let's check if everything is correct</h2>
              <p class="registrationPrompt__Text">
                Please check your submitted information and do corrections if there are any. Once you've submitted this
                form, you won't be able to update it until the approval process is done.
              </p>
              <p class="registrationPrompt__Text registrationPrompt__Text--Small">
                You can update all links and info anytime once you are logged in to the platform.
              </p>
            </template>
            <div class="registrationForm__ErrorBox" v-if="errors.org.length > 0 || errors.user.length > 0">
              <error-prompt class="error--Form" :errors="errors.org"></error-prompt>
              <error-prompt class="error--Form" :errors="errors.user"></error-prompt>
            </div>
          </div>
        </div>
        <!-- Inputs -->
        <div class="registrationColumn" :class="{ 'registrationColumn--Image': step === 2 }">
          <div class="registrationColumn__Inner">
            <div v-if="step === 1">
              <div class="register__Row">
                <label class="formLabel" for="organisations">
                  {{ $store.getters.getStringTranslation('organisation') }}
                </label>
                <div class="formSearch" :class="{ 'is-Disabled': lockSearch }">
                  <div class="formSearch__InputWrapper">
                    <icon class="formSearch__Icon" name="users"></icon>
                    <input
                      id="organisations"
                      class="formInput--Search"
                      type="text"
                      @focus="focused = true"
                      @blur="focused = false"
                      v-model="orgSearch"
                      :placeholder="$store.getters.getStringTranslation('enterOrgName')"
                      autocomplete="off"
                      :disabled="lockSearch"
                    />
                    <span @click="removeOrganisation()" class="formSearch__Remove" v-if="lockSearch">
                      <icon weight="regular" name="times"></icon>
                      {{ $store.getters.getStringTranslation('remove') }}
                    </span>
                  </div>
                  <ul
                    class="formSearch__List formSearch__List--Top"
                    :class="{ 'is-Active': focused === true }"
                    v-if="orgSearch.length > 1"
                  >
                    <li class="formSearch__ListItem" v-for="org in orgFilter" :key="org.id">
                      <span @click="pushOrganisations(org)">
                        <strong>{{ org.abbreviation }}</strong> {{ org.full_name }}
                      </span>
                    </li>
                    <li class="formSearch__ListItem--New">
                      <icon name="plus"></icon>
                      <span @click="createOrganisation()">
                        {{ $store.getters.getStringTranslation('createNewOrg') }}: {{ orgSearch }}
                      </span>
                    </li>
                  </ul>
                </div>
                <span class="note note--Events"
                  ><strong>Search on any language.</strong> Use the full name or abbreviation.
                </span>
              </div>
              <template v-if="registerUserOnly">
                <div class="register__Row">
                  <label class="formLabel" for="name">{{
                    $store.getters.getStringTranslation('firstNameLastName')
                  }}</label>
                  <input required class="formInput" type="text" id="name" v-model="registerData.user.name" />
                </div>
                <div class="register__Row">
                  <label class="formLabel" for="email">{{ $store.getters.getStringTranslation('yourEmail') }}</label>
                  <input required class="formInput" type="email" id="email" v-model="registerData.user.email" />
                </div>
                <div class="register__Row">
                  <label class="formLabel" for="passowrd">{{ $store.getters.getStringTranslation('password') }}</label>
                  <input
                    required
                    class="formInput"
                    type="password"
                    id="password"
                    v-model="registerData.user.password"
                  />
                </div>
                <div class="register__Row">
                  <label class="formLabel" for="countries">{{ $store.getters.getStringTranslation('country') }}</label>
                  <search-tag
                    :types="['country']"
                    :multiple="false"
                    :lang="lang"
                    v-model="registerData.user.country_id"
                    :tags="selectedCountry"
                    :placeholder="$store.getters.getStringTranslation('selectCountry')"
                    :errorMessage="$store.getters.getStringTranslation('noCountries')"
                    :showLabel="false"
                    :limit="false"
                  />
                </div>
                <div class="register__Row">
                  <button class="button--Registration" :disabled="!lockSearch" type="submit" @click="register()">
                    Register
                  </button>
                </div>
              </template>
              <div class="register__Row" v-else>
                <button class="button--Registration" :disabled="!lockSearch" type="submit" @click.prevent="nextStep(2)">
                  Start registration
                </button>
              </div>
            </div>

            <template v-if="step === 2">
              <img src="@/assets/images/icon-pop_p.svg" alt="" />
              <button class="button--Registration" @click.prevent="nextStep(3)">Start registration - Basic Info</button>
            </template>

            <template v-if="step > 2">
              <div v-show="step === 4 || step === 7">
                <h3 class="registerForm__Heading" v-if="step === 7">User info:</h3>

                <div class="register__Row">
                  <label class="formLabel" for="name">{{
                    $store.getters.getStringTranslation('firstNameLastName')
                  }}</label>
                  <input required class="formInput" type="text" id="name" v-model="registerData.user.name" />
                </div>
                <div class="register__Row">
                  <label class="formLabel" for="email">{{ $store.getters.getStringTranslation('yourEmail') }}</label>
                  <input required class="formInput" type="email" id="email" v-model="registerData.user.email" />
                </div>
                <div class="register__Row">
                  <label class="formLabel" for="passowrd">{{ $store.getters.getStringTranslation('password') }}</label>
                  <input
                    required
                    class="formInput"
                    type="password"
                    id="password"
                    v-model="registerData.user.password"
                  />
                </div>
                <div class="register__Row">
                  <label class="formLabel" for="countries">Your country</label>
                  <search-tag
                    :types="['country']"
                    :multiple="false"
                    :lang="lang"
                    v-model="registerData.user.country_id"
                    :tags="selectedCountryUser"
                    :placeholder="$store.getters.getStringTranslation('selectCountry')"
                    :errorMessage="$store.getters.getStringTranslation('noCountries')"
                    :showLabel="false"
                    :limit="false"
                  />
                </div>
              </div>

              <div v-show="step === 5 || step === 7">
                <h3 class="registerForm__Heading registerForm__Heading--MarginTop" v-if="step === 7">
                  About organisation:
                </h3>

                <div class="register__Row">
                  <label class="formLabel" for="org_name">Your organisation name</label>
                  <input required class="formInput" type="text" id="org_name" v-model="registerData.org.full_name" />
                </div>

                <div class="register__Row">
                  <label class="formLabel" for="org_email">Organisation email</label>
                  <input required class="formInput" type="text" id="org_email" v-model="registerData.org.email" />
                </div>

                <div class="register__Row">
                  <label class="formLabel" for="org_description">Organisation description</label>
                  <textarea
                    required
                    class="formInput"
                    type="text"
                    id="org_description"
                    v-model="registerData.org.description"
                    rows="4"
                    cols="50"
                  />
                </div>

                <div class="register__Row">
                  <label class="formLabel" for="org_country">Organisation country</label>
                  <search-tag
                    id="org_country"
                    :types="['country']"
                    :multiple="false"
                    :lang="lang"
                    v-model="registerData.org.country_id"
                    :tags="selectedCountryOrg"
                    :placeholder="$store.getters.getStringTranslation('selectCountry')"
                    :errorMessage="$store.getters.getStringTranslation('noCountries')"
                    :showLabel="false"
                    :limit="false"
                  />
                </div>

                <div class="register__Row">
                  <label class="formLabel" for="org_issues">Which issue will your campaign on?</label>
                  <search-tag
                    id="org_issues"
                    :types="['issue']"
                    :multiple="true"
                    :lang="lang"
                    v-model="registerData.org.issues"
                    :tags="selectedIssues"
                    :placeholder="$store.getters.getStringTranslation('chooseissue')"
                    errorMessage="No issues"
                    :showLabel="false"
                    :limit="false"
                  />
                </div>

                <div class="register__Row">
                  <label class="formLabel" for="org_lang">Main language used</label>
                  <select class="formSelect" v-model="registerData.org.lang" required>
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                  </select>
                </div>
              </div>

              <div v-show="step === 3 || step === 7">
                <h3 class="registerForm__Heading registerForm__Heading--MarginTop" v-if="step === 7">
                  Organisation structure:
                </h3>
                <div class="register__Row--CheckboxReverse">
                  <input
                    class="formCheckbox__Checkbox"
                    type="checkbox"
                    name="editPermission"
                    id="isOrganisation"
                    value="isOrganisation"
                    v-model="registerData.org.contents.additionals.is_org"
                  />
                  <label class="formCheckbox__Label" for="isOrganisation">We are an organisation</label>
                </div>

                <p class="registrationPrompt__Text">
                  An organisation has permanence beyond the people within it. It carries values, traditions and work
                  methods. It builds a presence created by the people who work there, but is more long lasting.
                </p>
                <p class="registrationPrompt__Text">
                  An organisation also has a legal identity, which both facilitates and limits what it can do. For using
                  the platform, each organisation will have legal responsibility for what it makes public.
                </p>

                <div class="register__Row--CheckboxReverse">
                  <input
                    class="formCheckbox__Checkbox"
                    type="checkbox"
                    name="editPermission"
                    id="nonProfit"
                    v-model="registerData.org.contents.additionals.non_profit"
                  />
                  <label class="formCheckbox__Label" for="nonProfit">We are not for profit</label>
                </div>

                <div class="register__Row--CheckboxReverse">
                  <input
                    class="formCheckbox__Checkbox"
                    type="checkbox"
                    name="editPermission"
                    id="nonPolitical"
                    value="nonPolitical"
                    v-model="registerData.org.contents.additionals.non_political"
                  />
                  <label class="formCheckbox__Label" for="nonPolitical">We are not a political party</label>
                </div>

                <div class="register__Row--CheckboxReverse">
                  <input
                    class="formCheckbox__Checkbox"
                    type="checkbox"
                    name="editPermission"
                    id="publicInterest"
                    value="publicInterest"
                    v-model="registerData.org.contents.additionals.public_interest"
                  />
                  <label class="formCheckbox__Label" for="publicInterest">We are fighting in public interest</label>
                </div>
              </div>

              <div v-show="step === 6 || step === 7">
                <h3 class="registerForm__Heading registerForm__Heading--MarginTop" v-if="step === 7">
                  Organisation info:
                </h3>
                <div class="register__Row">
                  <label class="formLabel" for="org_address">Organisation address</label>
                  <input required class="formInput" type="text" id="org_address" v-model="registerData.org.address" />
                </div>
                <div class="register__Row">
                  <label class="formLabel" for="org_website">Website url</label>
                  <input required class="formInput" type="text" id="org_website" v-model="registerData.org.website" />
                </div>
                <div class="register__Row">
                  <label class="formLabel" for="org_phone">Phone number</label>
                  <input
                    required
                    class="formInput"
                    type="text"
                    id="org_phone"
                    v-model="registerData.org.phone_number"
                  />
                </div>
                <div class="register__Row">
                  <label class="formLabel">Organization document:</label>
                  <div class="simpleFileUpload">
                    <div class="simpleFileUpload__Choose">
                      <label for="simple-file-upload" class="simpleFileUpload__Label">Choose document</label>
                      <input
                        type="file"
                        id="simple-file-upload"
                        accept="image/*, application/pdf, application/msword, application/vnd.ms-excel"
                        @change="handleFileSelect"
                        class="simpleFileUpload__Input"
                        required
                      />
                    </div>
                    <div v-for="file in selectedFiles" :key="file.id" class="simpleFileUpload__FileName">
                      <span>
                        {{ file.name }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="register__Row" v-if="registerData.org.source">
                  <label class="formLabel" for="facebook">Facebook url</label>
                  <input
                    required
                    class="formInput"
                    type="text"
                    id="org_phone"
                    v-model="registerData.org.source.facebook"
                  />
                </div>

                <div class="register__Row" v-if="registerData.org.source">
                  <label class="formLabel" for="twitter">Twitter url</label>
                  <input
                    required
                    class="formInput"
                    type="text"
                    id="twitter"
                    v-model="registerData.org.source.twitter"
                  />
                </div>
              </div>
            </template>

            <div class="register__Row--Buttons" v-if="step > 2 && step < 7">
              <button class="button--Registration" @click.prevent="prevStep()">Back</button>
              <button class="button--Accent" @click.prevent="nextStep()">Next</button>
            </div>

            <div class="register__Row--Buttons" v-if="step === 7">
              <button class="button--Registration" @click="prevStep()">Back</button>
              <button class="button--Accent" @click.prevent="register()">Register</button>
            </div>
          </div>
        </div>
      </div>
    </form>

    <div v-if="step === 8" class="regSuccess">
      <div class="regSuccess__Background"></div>
      <div class="regSuccess__Content">
        <div class="regSuccess__Col regSuccess__Left">
          <h2 class="registrationPrompt__Title">Thank you! You'll hear from us in two working days.</h2>
          <p class="registrationPrompt__Text">
            We'll contact you at the email address you provided. We'll check your information and see if we share the
            same values.
          </p>
          <p class="registrationPrompt__Text">
            We are a small team, and in general we will keep the promise of two working days, but stuff happens, and
            we'll do our best.
          </p>
          <p class="registrationPrompt__Text">
            Depending on the information you've provided, your request might be denied. This may be because we're not a
            good fit or we miss some info. If you've submitted incomplete information, we'll contact you to resolve the
            situation.
          </p>

          <p class="registrationPrompt__Text">
            <router-link :to="{ name: 'Login' }">{{ $store.getters.getStringTranslation('backToLogin') }}</router-link>
          </p>
        </div>
        <div class="regSuccess__Col regSuccess__Right">
          <img src="@/assets/images/icon-pop_p.svg" alt="" />
        </div>
      </div>
    </div>

    <loader class="imageLoader" :loading="loading" />
  </section>
</template>

<script>
import HTTP from '@/http-common.js'

import SearchTag from '@/components/editor/blocks/SearchTag.vue'
import Loader from '@/components/partials/Loader.vue'
import GetProperty from '@/components/mixins/GetProperty.js'
import ErrorPrompt from '@/components/editor/ErrorPrompt.vue'

export default {
  name: 'Register',
  components: {
    SearchTag,
    Loader,
    ErrorPrompt,
  },
  mixins: [GetProperty],
  data() {
    return {
      selectedFiles: [],
      step: 1,
      stepCount: 7,
      loading: false,
      selectedCountryOrg: [],
      selectedCountryUser: [],
      selectedIssues: [],
      orgSearch: '',
      lockSearch: false,
      exists: false,
      registerUserOnly: false,
      registerData: {
        api_keys: ['bee095f2-1525-477f-bc90-640a3e41a8de'],
        user: {
          email: '',
          password: '',
          name: '',
          country_id: null,
          contents: {
            additionals: {},
          },
        },
      },
      errors: {
        org: [],
        user: [],
      },
      success: {
        text: '',
      },
      focused: false,
    }
  },
  watch: {
    registerData: {
      handler() {
        this.errors = {
          org: [],
          user: [],
        }
      },
      deep: true,
    },
  },
  methods: {
    handleFileSelect(event) {
      this.selectedFiles = Array.from(event.target.files)
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1)
    },
    uploadFiles(org_id) {
      const formData = new FormData()
      formData.append('file', this.selectedFiles[0])
      formData.append('org_id', org_id)
      formData.append('hidden', true)

      HTTP.post(`upload`, formData)
        .then((response) => {
          console.log(response)
          this.loading = false
          this.step = 8
        })
        .catch((e) => {
          this.errors = e.response ? e.response.data : console.error(e)
          this.loading = false
        })
    },
    register() {
      this.loading = true

      HTTP.post('signup', this.registerData)
        .then((response) => {
          this.errors = {
            org: [],
            user: [],
          }
          this.uploadFiles(response.data.org.id)
        })
        .catch((e) => {
          this.success.text = ''
          this.errors = e.response ? e.response.data : console.error(e)
          this.loading = false
        })
    },
    pushOrganisations(org) {
      this.exists = false
      this.registerUserOnly = true
      this.lockSearch = true
      this.orgSearch = org.full_name
      this.$set(this.registerData.user, 'org_id', org.id)
    },
    createOrganisation() {
      this.exists = false
      this.registerUserOnly = false
      let inUse = false
      this.orgs.filter((org) => {
        if (org.full_name.toLowerCase() === this.orgSearch.toLowerCase()) {
          this.exists = true
          inUse = true
        }
      })

      if (!inUse) {
        this.lockSearch = true
        const data = {
          full_name: this.orgSearch,
          abbreviation: '',
          address: '',
          country_id: '',
          email: this.registerData.user.email,
          phone_number: '',
          thumbnail_url: '',
          description: '',
          document: {},
          source: {
            facebook: '',
            twitter: '',
          },
          contents: {
            additionals: {
              is_org: false,
              non_profit: false,
              non_political: false,
              public_interest: false,
            },
          },
        }

        this.$set(this.registerData, 'org', data)
      }
    },
    removeOrganisation() {
      this.exists = false
      this.registerUserOnly = false
      if (this.registerData.org) delete this.registerData.org
      if (this.registerData.user.org_id) delete this.registerData.user.org_id
      this.lockSearch = false
      this.orgSearch = ''
    },
    nextStep() {
      this.step = this.step + 1
    },
    prevStep() {
      this.step = this.step - 1
    },
  },
  computed: {
    lang() {
      return this.$store.state.ui.lang
    },
    orgs() {
      return this.$store.state.accounts.orgs
    },
    orgFilter() {
      return this.orgs.filter((org) => {
        return `${org.full_name} (${org.abbreviation})`.toLowerCase().includes(this.orgSearch.toLowerCase())
      })
    },
    query() {
      if (this.$route.query) return this.$route.query
      return ''
    },
  },
  mounted() {
    this.$store.dispatch('loadOrgs')
    if (this.query.api_keys) this.registerData.api_keys = JSON.parse(this.query.api_keys)
  },
}
</script>
